import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Link,
  Paper,
  Typography,
} from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { saveOaaOrgs } from '@/api';
import { AvailityHeaderLogo, Footer, OAAOrganizationsTable } from '@/components';
import { useAppContext } from '@/context';
import { handleSuccessNavigation, trackEvent } from '@/global';

// handle getting the av portal urls
const { host = '' } = window.location;
const lowerCaseHost = host?.toLowerCase();
// check if the page is loaded with '.net' and set the proper domain suffix
const isDotNet = lowerCaseHost.indexOf('.net') >= 0;
const domainSuffix = isDotNet ? '.availity.net' : '.availity.com';

const currentEnv = getSpecificEnv();
const oaaBaseUrl =
  currentEnv?.toLowerCase() === 'prod' ? `https://apps${domainSuffix}` : `https://${currentEnv}-apps${domainSuffix}`;

const oaaAgreementUrl = `${oaaBaseUrl}/availity/documents/Availity_OAA.pdf`;

const OAACheckboxLabel = ({ ...props }) => {
  const { selectedOAAOrgIds, error } = props;

  return (
    <Box sx={{ bgcolor: error && '#C70002' }}>
      I agree to be the primary administrator for the{' '}
      {selectedOAAOrgIds.length > 1 ? `${selectedOAAOrgIds.length} organizations` : 'organization'} above, and I have
      the organization&apos;s authority to, and do, accept{' '}
      <Link
        href={oaaAgreementUrl}
        target="_blank"
        loadApp={false}
        aria-label="See OAA (opens PDF in a new window)"
        sx={{ marginLeft: 1 }}
      >
        Availity&apos;s Organization Agreement
      </Link>
      .
    </Box>
  );
};

OAACheckboxLabel.propTypes = {
  selectedOAAOrgIds: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.object,
};

const OAASelectedOrganizations = ({ ...props }) => {
  const { setCurrentOAAPage, setSkipBackPage, hasCheckedOAABox, setHasCheckedOAABox } = props;
  const { setLoading, queryParams = {}, oaaOrgsResponse, selectedOAAOrgIds, userHasInvites } = useAppContext();

  const { goto } = queryParams;

  const { oaaOrganizations, skipCount = 0 } = oaaOrgsResponse;

  // only show the skip button if skip count is less than 3
  const showSkipButton = useMemo(() => skipCount < 3, [skipCount]);

  const selectedOrgCount = useMemo(() => selectedOAAOrgIds.length, [selectedOAAOrgIds]);
  const totalOrgCount = useMemo(() => oaaOrganizations.length, [oaaOrganizations]);

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      agreed: hasCheckedOAABox || '',
    },
  });

  const oaaCheckboxValue = watch('agreed');

  // build out organizations for display based on selections from the admin check-in page
  const organizationsToDisplay = useMemo(
    () => oaaOrganizations.filter((organization) => selectedOAAOrgIds.indexOf(organization?.appId) >= 0),
    [oaaOrganizations, selectedOAAOrgIds]
  );

  // build payload for selected organizations
  const selectedOrgsPayload = useMemo(() => {
    let output;

    if (selectedOAAOrgIds) {
      const oAAAcknowledgements = [];

      for (const appId of selectedOAAOrgIds) {
        oAAAcknowledgements.push({
          appId,
          oAAAcknowledgementIn: 'Y',
        });
      }

      output = { oAAAcknowledgements, isSkip: false };
    }

    return output;
  }, [selectedOAAOrgIds]);

  // handle skip OAA logic
  const handleSkip = () => {
    setSkipBackPage('viewSelectedOrgs');
    setHasCheckedOAABox(!!oaaCheckboxValue);
    setCurrentOAAPage('skipOAA');
  };

  // handle back navigation
  const handleBack = () => {
    setHasCheckedOAABox(!!oaaCheckboxValue);
    setCurrentOAAPage('adminCheckIn');
  };

  // handle form submit
  const onSubmit = async () => {
    setLoading(true);
    try {
      await saveOaaOrgs(selectedOrgsPayload);
    } catch (error) {
      trackEvent({
        api: 'user-org-management-api',
        event: 'save-oaa-selected-orgs-failure',
        status: error?.response?.status,
      });
    }

    setLoading(false);

    // only go through adminChange flow if unselected orgs exist. otherwise, handle usual success navigation
    if (selectedOAAOrgIds.length < oaaOrganizations.length) {
      setCurrentOAAPage('adminChange');
    } else {
      handleSuccessNavigation(goto, userHasInvites);
    }
  };

  return (
    <Grid
      id="oaa-selected-organizations-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid size={{ xs: 12, sm: 12, md: 6 }} display="flex" flexDirection="column" alignItems="center" flexGrow={1}>
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Paper
              variant="elevation"
              elevation={6}
              sx={{ width: { xs: '350px', sm: '500px', md: '900px', lg: '1200px' } }}
            >
              <CardContent>
                <Typography variant="h1" mb={2}>
                  Availity Essentials Organization Access Agreement (OAA)
                </Typography>
                <Typography variant="subtitle1" mb={2}>
                  You have elected to be the primary administrator of {selectedOrgCount} out of {totalOrgCount}{' '}
                  organizations. Review the table below and click the checkbox to submit the OAA for the selected
                  organizations.
                </Typography>
                <OAAOrganizationsTable organizations={organizationsToDisplay} selectable={false} />
                <FormControl error={errors.agreed}>
                  <FormGroup>
                    <FormControlLabel
                      id="oaa-agreement-checkbox-label"
                      control={
                        <Checkbox
                          checked={!!oaaCheckboxValue}
                          {...register('agreed', {
                            required:
                              'You must accept the Availity Essentials Organization Access Agreement (OAA) for the above organizations.',
                          })}
                        />
                      }
                      error={errors.agreed}
                      label={<OAACheckboxLabel selectedOAAOrgIds={selectedOAAOrgIds} />}
                      sx={{ marginTop: 3 }}
                    />
                    {errors.agreed?.message && (
                      <FormHelperText id="oaa-agreement-checkbox-helpertext">{errors.agreed?.message}</FormHelperText>
                    )}
                  </FormGroup>
                </FormControl>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', marginTop: 1 }}>
                {showSkipButton ? (
                  <Button color="tertiary" onClick={handleSkip}>
                    I&apos;ll do this later
                  </Button>
                ) : (
                  <Box />
                )}
                <Box>
                  <Button color="secondary" onClick={handleBack}>
                    Back
                  </Button>{' '}
                  <Button color="primary" type="submit">
                    Submit
                  </Button>
                </Box>
              </CardActions>
            </Paper>
          </form>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

OAASelectedOrganizations.propTypes = {
  setCurrentOAAPage: PropTypes.func.isRequired,
  setSkipBackPage: PropTypes.func.isRequired,
  hasCheckedOAABox: PropTypes.bool.isRequired,
  setHasCheckedOAABox: PropTypes.func.isRequired,
};

export default OAASelectedOrganizations;
